import React, { useState } from 'react';
import { useSmsmessagesCustomerLiveSubscription } from '../queries/smsmessagesCustomerLive.subscription.generated';
import { useUser, useRooftop } from '@store';
import { useValetSelfManaged } from '@hopdrive/feature-portal-products';
import { useApolloClient } from '@apollo/client';
import { GetSmsmessagesDocument } from '../queries/getSmsmessages.query.generated';
import { GetAllSmsMessagesDocument } from '../queries/getAllSmsMessages.query.generated';

interface GlobalSmsSubscriptionProviderProps {
  children: React.ReactNode;
  enabled?: boolean; // Allow disabling for testing/debugging
}

/**
 * Global SMS Subscription Provider
 * 
 * This component provides app-wide real-time SMS message updates by subscribing 
 * to all SMS messages for the user's allowed customers and updating the Apollo cache.
 * This eliminates the need for polling in individual components.
 */
export const GlobalSmsSubscriptionProvider: React.FC<GlobalSmsSubscriptionProviderProps> = ({
  children,
  enabled = true,
}) => {
  const user = useUser();
  const rooftop = useRooftop();
  const isSelfManaged = useValetSelfManaged();
  
  // Scope this subscription to the SELECTED rooftop only.
  //
  // smsmessagesCustomerLive has no `limit` and no time window: it returns every SMS message
  // ever sent for every consumer_pickup move belonging to each customer id passed in, and
  // re-delivers the whole set on each emission. It previously received the user's entire
  // `x-hasura-allowed-customers` claim — hundreds of rooftops for an org-level user — which
  // made this the whole organisation's message history, streamed on every route, since the
  // provider is mounted in Layout.
  //
  // The app only ever displays messages for the active rooftop, so ask for that.
  const rooftopId = rooftop?.id;
  // Fallback for when no rooftop has resolved yet: the user's own customer, rather than the
  // full claim list, so a slow rooftop load can't briefly open the org-wide stream.
  const fallbackCustomerId = user?.profile?.customer_id;
  const allowedCustomerIds = React.useMemo(
    () => (rooftopId ? [rooftopId] : fallbackCustomerId ? [fallbackCustomerId] : []),
    [rooftopId, fallbackCustomerId]
  );
  const apolloClient = useApolloClient();
  const [messageCount, setMessageCount] = useState(0);
  const [lastUpdateTime, setLastUpdateTime] = useState<string | null>(null);
  const prevMessageIds = React.useRef<Set<number>>(new Set());
  const mountTime = React.useRef(new Date().toISOString());
  // Whether we've taken delivery of the initial backlog yet (see onSubscriptionData).
  const seeded = React.useRef(false);

  // Constants for memory management
  const MAX_MESSAGE_IDS = 10000; // Limit Set size to prevent unbounded growth

  // Subscribe to ALL SMS messages for the allowed customers
  // This subscribes to all messages for moves with consumer_pickup
  // Apollo will handle cache normalization and updates
  const { error, loading } = useSmsmessagesCustomerLiveSubscription({
    variables: {
      customerIds: allowedCustomerIds,
    },
    skip: !enabled || !allowedCustomerIds.length || !isSelfManaged,
    errorPolicy: 'all',
    onSubscriptionData: ({ subscriptionData }) => {
      const receiveTime = new Date().toISOString();

      if (subscriptionData.data?.smsmessages) {
        const messages = subscriptionData.data.smsmessages;

        // The first emission is the ENTIRE backlog, because this subscription is unbounded.
        // Treating it as "new" meant mount did a per-move Apollo read + write for every move
        // that has ever had a message — the dominant cost on a high-volume rooftop. Seed the
        // id set from it and stop: each move's own getSmsmessages query populates its cache
        // entry, and the per-move patching below is only needed for genuinely subsequent
        // messages. Server orders createdat desc, so the head of the list is the newest.
        if (!seeded.current) {
          seeded.current = true;
          prevMessageIds.current = new Set(messages.slice(0, MAX_MESSAGE_IDS).map(m => m.id));
          setMessageCount(messages.length);
          setLastUpdateTime(receiveTime);
          return;
        }

        // Detect new messages across all moves
        const newMessages = messages.filter(m => !prevMessageIds.current.has(m.id));

        // Early exit if no new messages - avoid redundant work
        if (newMessages.length === 0) {
          return;
        }

        // Update tracking with size limit to prevent memory leaks
        const newMessageIds = new Set(messages.map(m => m.id));
        if (newMessageIds.size > MAX_MESSAGE_IDS) {
          // Keep only the most recent message IDs
          const sortedMessages = [...messages].sort((a, b) => {
            const dateA = a.createdat ? new Date(a.createdat).getTime() : 0;
            const dateB = b.createdat ? new Date(b.createdat).getTime() : 0;
            return dateB - dateA; // Descending - newest first
          });
          prevMessageIds.current = new Set(sortedMessages.slice(0, MAX_MESSAGE_IDS).map(m => m.id));
        } else {
          prevMessageIds.current = newMessageIds;
        }

        setMessageCount(messages.length);
        setLastUpdateTime(receiveTime);

        // CRITICAL: Manually update individual move caches and batch caches
        // Get unique move IDs that have NEW messages only
        // Type predicate rather than `.filter(Boolean)` so this narrows to number[].
        const affectedMoveIds = [
          ...new Set(
            newMessages
              .map(m => m.move_id)
              .filter((id): id is number => id !== null && id !== undefined)
          ),
        ];

        // Sort messages once for all moves
        // Copy before sorting: .sort() mutates in place, and `messages` is the subscription
        // payload that `newMessages` was filtered from above — keep that ordering dependency
        // from ever mattering.
        const sortedMessages = [...messages].sort((a, b) => {
          const dateA = a.createdat ? new Date(a.createdat).getTime() : 0;
          const dateB = b.createdat ? new Date(b.createdat).getTime() : 0;
          return dateA - dateB; // Ascending order for chat display
        });

        // Bucket messages by move ONCE — O(messages) — instead of re-scanning the whole
        // array per affected move, which made this loop O(moves x messages) with an Apollo
        // cache read and write inside it.
        const messagesByMove = new Map<number, typeof sortedMessages>();
        for (const msg of sortedMessages) {
          if (msg.move_id === null || msg.move_id === undefined) continue;
          const bucket = messagesByMove.get(msg.move_id);
          if (bucket) bucket.push(msg);
          else messagesByMove.set(msg.move_id, [msg]);
        }

        // 1. For each affected move, update its individual cache with only its messages
        affectedMoveIds.forEach(moveId => {
          const moveMessages = messagesByMove.get(moveId) || [];

          try {
            // Read existing cache for this move (if it exists)
            let existingData;
            try {
              existingData = apolloClient.readQuery({
                query: GetSmsmessagesDocument,
                variables: { moveId }
              });
            } catch (e) {
              // Cache doesn't exist yet, which is fine
              existingData = null;
            }

            // Update the individual move query cache with complete message set
            apolloClient.writeQuery({
              query: GetSmsmessagesDocument,
              variables: { moveId },
              data: {
                smsmessages: moveMessages
              }
            });
          } catch (error) {
            // Cache update failed - silent failure
          }
        });

        // 2. Update batch queries (for UnreadMessagesDropdown)
        // We need to update the GetAllSmsMessages query cache that UnreadMessagesDropdown uses
        try {
          // For each batch of move IDs that might be cached, update the query
          // This handles the getAllSmsMessages query used by UnreadMessagesDropdown
          const allMoveIds = affectedMoveIds;

          if (allMoveIds.length > 0) {
            // Check if there's an existing batch query in cache
            try {
              // Try to read any existing batch query
              const existingBatchData = apolloClient.readQuery({
                query: GetAllSmsMessagesDocument,
                variables: { moveIds: allMoveIds }
              });

              // If we have existing data, update it
              if (existingBatchData) {
                apolloClient.writeQuery({
                  query: GetAllSmsMessagesDocument,
                  variables: { moveIds: allMoveIds },
                  data: {
                    smsmessages: sortedMessages.filter(m => m.move_id !== null && m.move_id !== undefined && allMoveIds.includes(m.move_id)),
                    moves: existingBatchData.moves // Keep existing moves data
                  }
                });
              }
            } catch (e) {
              // No existing batch query, that's fine
            }

            // Dispatch targeted event for SMS-specific components only
            // Include specific move IDs so components can check if they're affected
            window.dispatchEvent(new CustomEvent('sms-messages-updated', {
              detail: {
                affectedMoveIds: affectedMoveIds,
                messageCount: sortedMessages.length,
                newMessageCount: newMessages.length,
                hasNewMessages: newMessages.length > 0
              }
            }));
          }
        } catch (error) {
          // Batch cache update failed - silent failure
        }
      }
    },
    onSubscriptionComplete: () => {
      // Subscription completed
    },
  });

  // Error handling - silent failures allow app to continue
  React.useEffect(() => {
    if (error) {
      console.error(`[GlobalSmsSubscriptionProvider] Subscription error:`, error);
    }
  }, [error]);

  // Render children - subscription runs as side effect
  return <>{children}</>;
};

export default GlobalSmsSubscriptionProvider;